import { useState } from 'react';
import { Send, Loader2, CheckCircle, AlertCircle, Phone, Mail, MapPin } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.name && formData.email && formData.phone && formData.message;

  return (
    <section 
      id="contact" 
      className="py-20 md:py-32 bg-zinc-900"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <span 
              className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-400 mb-4 block"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Contáctenos
            </span>
            <h2 
              className="text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white mb-6"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              data-testid="contact-title"
            >
              ¿Tiene Preguntas?
            </h2>
            <p className="text-zinc-400 text-lg mb-10 leading-relaxed">
              Estamos aquí para ayudarle. Envíenos un mensaje y nos pondremos en contacto 
              lo antes posible, o llámenos directamente.
            </p>

            {/* Contact Cards */}
            <div className="space-y-4">
              <a 
                href="tel:+18628327369"
                className="flex items-center gap-4 p-4 bg-zinc-950 border border-zinc-800 hover:border-blue-600 transition-colors group"
                data-testid="contact-phone-card"
              >
                <div className="w-14 h-14 bg-blue-600/20 border border-blue-600/40 flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                  <Phone className="text-yellow-400 group-hover:text-white transition-colors" size={24} />
                </div>
                <div>
                  <p className="text-zinc-500 text-sm uppercase tracking-wider">Teléfono / WhatsApp</p>
                  <p className="text-white font-bold text-lg">+1 (862) 832-7369</p>
                </div>
              </a>

              <div 
                className="flex items-center gap-4 p-4 bg-zinc-950 border border-zinc-800"
                data-testid="contact-address-card"
              >
                <div className="w-14 h-14 bg-blue-600/20 border border-blue-600/40 flex items-center justify-center">
                  <MapPin className="text-yellow-400" size={24} />
                </div>
                <div>
                  <p className="text-zinc-500 text-sm uppercase tracking-wider">Dirección</p>
                  <p className="text-white font-bold">280 W Clinton St.</p>
                  <p className="text-zinc-400">Dover, NJ 07801</p>
                </div>
              </div>
            </div>

            {/* Map Embed */}
            <div className="mt-8 border border-zinc-800 overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3019.8!2d-74.5628!3d40.8839!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c307!2s280%20W%20Clinton%20St%2C%20Dover%2C%20NJ%2007801!5e0!3m2!1sen!2sus!4v1"
                width="100%"
                height="200"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="MV Auto Expert Location"
                data-testid="contact-map"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-zinc-950 border border-zinc-800 p-8">
            <h3 
              className="text-2xl font-bold uppercase text-white mb-6"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Envíenos Un Mensaje
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label className="block text-sm font-bold uppercase tracking-wider text-zinc-400 mb-2">
                  Nombre *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full bg-zinc-900 border border-zinc-800 text-zinc-100 p-4 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                  placeholder="Su nombre"
                  data-testid="contact-input-name"
                />
              </div>

              <div>
                <label className="block text-sm font-bold uppercase tracking-wider text-zinc-400 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full bg-zinc-900 border border-zinc-800 text-zinc-100 p-4 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                  placeholder="su@email.com"
                  data-testid="contact-input-email"
                />
              </div>

              <div>
                <label className="block text-sm font-bold uppercase tracking-wider text-zinc-400 mb-2">
                  Teléfono *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full bg-zinc-900 border border-zinc-800 text-zinc-100 p-4 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                  placeholder="+1 (555) 123-4567"
                  data-testid="contact-input-phone"
                />
              </div>

              <div>
                <label className="block text-sm font-bold uppercase tracking-wider text-zinc-400 mb-2">
                  Mensaje *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  className="w-full bg-zinc-900 border border-zinc-800 text-zinc-100 p-4 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all resize-none"
                  placeholder="¿En qué podemos ayudarle?"
                  data-testid="contact-input-message"
                />
              </div>

              <button
                type="submit"
                disabled={!isFormValid || isSubmitting}
                className={`w-full font-bold uppercase tracking-widest px-8 py-4 transition-all flex items-center justify-center gap-2 ${
                  isFormValid && !isSubmitting
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                }`}
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                data-testid="contact-submit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Enviar Mensaje
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="flex items-center gap-3 p-4 bg-green-900/30 border border-green-700 text-green-400" data-testid="contact-success">
                  <CheckCircle size={20} />
                  <span>¡Mensaje enviado! Nos pondremos en contacto pronto.</span>
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="flex items-center gap-3 p-4 bg-red-900/30 border border-red-700 text-red-400" data-testid="contact-error">
                  <AlertCircle size={20} />
                  <span>Error al enviar. Por favor intente nuevamente.</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
